import React from "react";
import "./about.css";
import {
  FaJava,
  FaPython,
  FaReact,
  FaNodeJs,
  FaAws,
  FaDocker,
  FaGithub,
} from "react-icons/fa";
import {
  SiSpring,
  SiTypescript,
  SiJavascript,
  SiHtml5,
  SiCss3,
  SiFlutter,
  SiCplusplus,
  SiExpress,
  SiAngular,
  SiDjango,
  SiFlask,
  SiNextdotjs,
  SiCircleci,
  SiJenkins,
  SiKubernetes,
  SiPostgresql,
  SiMongodb,
  SiJunit5,
  SiNewrelic,
  SiGrafana,
  SiPostman,
  SiJira,
  SiConfluence,
  SiTerraform,
  SiMysql,
  SiRedis,
  SiElasticsearch,
} from "react-icons/si";
import ProfilePhoto from "assets/images/profile.png";
import Header from "components/Header";
import Footer from "components/Footer";

const About = () => {
  const techCategories = [
    {
      title: "Programming Languages",
      items: [
        { name: "Java", icon: <FaJava /> },
        { name: "TypeScript", icon: <SiTypescript /> },
        { name: "JavaScript", icon: <SiJavascript /> },
        { name: "Python", icon: <FaPython /> },
        { name: "C++", icon: <SiCplusplus /> },
        { name: "HTML5", icon: <SiHtml5 /> },
        { name: "CSS3", icon: <SiCss3 /> },
      ],
    },
    {
      title: "Frameworks & Libraries",
      items: [
        { name: "React", icon: <FaReact /> },
        { name: "Angular", icon: <SiAngular /> },
        { name: "Spring", icon: <SiSpring /> },
        { name: "Express", icon: <SiExpress /> },
        { name: "Django", icon: <SiDjango /> },
        { name: "Flask", icon: <SiFlask /> },
        { name: "Next.js", icon: <SiNextdotjs /> },
        { name: "Node.js", icon: <FaNodeJs /> },
        { name: "Flutter", icon: <SiFlutter /> },
      ],
    },
    {
      title: "Cloud & DevOps",
      items: [
        { name: "AWS", icon: <FaAws /> },
        { name: "Docker", icon: <FaDocker /> },
        { name: "Kubernetes", icon: <SiKubernetes /> },
        { name: "CircleCI", icon: <SiCircleci /> },
        { name: "Jenkins", icon: <SiJenkins /> },
        { name: "Terraform", icon: <SiTerraform /> },
      ],
    },
    {
      title: "Databases & Tools",
      items: [
        { name: "PostgreSQL", icon: <SiPostgresql /> },
        { name: "MongoDB", icon: <SiMongodb /> },
        { name: "MySQL", icon: <SiMysql /> },
        { name: "Redis", icon: <SiRedis /> },
        { name: "Elasticsearch", icon: <SiElasticsearch /> },
      ],
    },
    {
      title: "Monitoring & Testing",
      items: [
        { name: "New Relic", icon: <SiNewrelic /> },
        { name: "Grafana", icon: <SiGrafana /> },
        { name: "JUnit", icon: <SiJunit5 /> },
        { name: "Postman", icon: <SiPostman /> },
      ],
    },
    {
      title: "Development Tools",
      items: [
        { name: "GitHub", icon: <FaGithub /> },
        { name: "Jira", icon: <SiJira /> },
        { name: "Confluence", icon: <SiConfluence /> },
      ],
    },
  ];

  return (
    <div>
      <Header />
      <div className="about-container fade-in">
        <div className="about-hero">
          <h1>About Me</h1>
        </div>

        <div className="about-intro slide-in-left">
          <div className="about-profile-image">
            <img src={ProfilePhoto} alt="Vincent Shi - Software Engineer" />
          </div>
          <div className="about-text">
            <p>
              <strong>Hello! I'm Vincent Shi</strong>, a passionate Software &
              DevOps Engineer currently working at
              <strong> Macquarie Group</strong> - Australia's leading financial
              services organisations. I hold an Honours degree in Software
              Engineering and am currently pursuing a{" "}
              <strong>Master of Information Technology</strong>, both from the{" "}
              <strong>University of New South Wales (UNSW)</strong>.
            </p>
            <p>
              I'm driven by the challenge of building{" "}
              <strong>scalable applications</strong> and
              <strong> robust infrastructure</strong>. My expertise spans{" "}
              <strong>full-stack development</strong>,
              <strong> CI/CD pipeline</strong>, automation,{" "}
              <strong>cloud infrastructure</strong>,{" "}
              <strong>containerization</strong>, and ensuring{" "}
              <strong>high availability</strong> of critical systems.
            </p>
            <p>
              Originally from Xi'an 🇨🇳, I've called Sydney home since 2017 🇦🇺.
              When I'm not coding, you'll find me exploring the latest tech
              trends or enjoying Sydney's beautiful parks 🌳.
            </p>
          </div>
        </div>

        <div className="tech-skills slide-in-right">
          <h2>Technology Stack</h2>
          <div className="tech-categories">
            {techCategories.map((category, index) => (
              <div key={index} className="tech-category">
                <h3>{category.title}</h3>
                <div className="tech-grid">
                  {category.items.map((tech, techIndex) => (
                    <div key={techIndex} className="tech-item">
                      <div className="tech-icon">{tech.icon}</div>
                      <div className="tech-name">{tech.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="professional-section fade-in">
          <h2>Professional Expertise</h2>
          <p>
            My career has been focused on building scalable, maintainable
            software solutions. I specialize in full-stack development with
            particular strength in:
          </p>

          <div className="highlight-box">
            <strong>Full-Stack Development:</strong> React/Angular frontends
            with Java Spring/Node.js backends
            <br />
            <br />
            <strong>Cloud Architecture:</strong> AWS services including EC2,
            ECS, Lambda, API Gateway, and S3
            <br />
            <br />
            <strong>DevOps & CI/CD:</strong> Docker containerization, Kubernetes
            orchestration, and automated deployment pipelines
            <br />
            <br />
            <strong>Database Design:</strong> PostgreSQL, MongoDB, and Redis for
            various data storage needs
          </div>

          <p>
            I'm particularly passionate about{" "}
            <strong>clean code principles</strong>,
            <strong> test-driven development</strong>, and{" "}
            <strong>agile methodologies</strong>. My experience includes leading
            technical initiatives, mentoring junior developers, and architecting
            solutions that serve thousands of users.
          </p>

          <p>
            Currently exploring the frontiers of{" "}
            <strong>machine learning</strong> and
            <strong> natural language processing</strong>, always excited to
            integrate cutting-edge technologies into practical business
            solutions.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default About;
